import { create } from 'zustand';
import {
	ParkingLocationDto,
	ParkingVehicleQuote,
	ParkingDayAvailability,
} from 'Services/Api/_HumanWritten/ParkingService';

export type ParkingWizardStep = 'location' | 'vehicles' | 'review' | 'payment';

export interface ParkingVehicleEntry {
	parkingVehicleTypeId: string;
	parkingVehicleTypeName: string;
	startDate: string;
	endDate: string;
	registration?: string;
	make?: string;
	model?: string;
}

export interface ParkingVehicleDetail {
	registration: string;
	make: string;
	model: string;
	startDate: string;
	endDate: string;
	locationId?: string;
	parkingVehicleTypeId?: string;
}

interface ParkingWizardState {
	step: ParkingWizardStep;
	locationId: string | null;
	location: ParkingLocationDto | null;
	vehicles: ParkingVehicleEntry[];
	vehicleDetails: ParkingVehicleDetail[];
	availability: ParkingDayAvailability[];
	quote: ParkingVehicleQuote[] | null;
	totalPrice: number | null;
	transactionId: string | null;
	reservationId: string | null;
	reservationIds: string[];
	humanReadableId: string | null;
	humanReadableIds: string[];
	locationNames: Record<string, string>;
	vehicleTypeNames: Record<string, string>;
	note: string;
	selectedUserId: string | null;
	selectedCustomer: any | null;
	customerLocked: boolean;
	originalPrice: number | null;
	originalVehicles: ParkingVehicleEntry[];
	vehicleAvailabilityStatus: string | null;
	isLoading: boolean;
	error: string | null;
}

interface ParkingWizardActions {
	setStep: (step: ParkingWizardStep) => void;
	setLocation: (location: ParkingLocationDto) => void;
	addVehicle: (vehicle: ParkingVehicleEntry) => void;
	removeVehicle: (index: number) => void;
	updateVehicle: (index: number, vehicle: ParkingVehicleEntry) => void;
	setAvailability: (availability: ParkingDayAvailability[]) => void;
	setQuote: (vehicles: ParkingVehicleQuote[], totalPrice: number) => void;
	setVehicleDetails: (details: ParkingVehicleDetail[]) => void;
	setLocationNames: (names: Record<string, string>) => void;
	setVehicleTypeNames: (names: Record<string, string>) => void;
	setReservationResult: (
		reservationId: string,
		reservationIds: string[],
		transactionId: string,
		humanReadableId: string,
		humanReadableIds: string[],
	) => void;
	setNote: (note: string) => void;
	setSelectedUserId: (userId: string | null) => void;
	setSelectedCustomer: (customer: any | null) => void;
	lockCustomer: () => void;
	setOriginalPrice: (price: number) => void;
	setOriginalVehicles: (vehicles: ParkingVehicleEntry[]) => void;
	setVehicleAvailabilityStatus: (status: string | null) => void;
	setLoading: (isLoading: boolean) => void;
	setError: (error: string | null) => void;
	reset: () => void;
}

const initialState: ParkingWizardState = {
	step: 'location',
	locationId: null,
	location: null,
	vehicles: [],
	vehicleDetails: [],
	availability: [],
	quote: null,
	totalPrice: null,
	transactionId: null,
	reservationId: null,
	reservationIds: [],
	humanReadableId: null,
	humanReadableIds: [],
	locationNames: {},
	vehicleTypeNames: {},
	note: '',
	selectedUserId: null,
	selectedCustomer: null,
	customerLocked: false,
	originalPrice: null,
	originalVehicles: [],
	vehicleAvailabilityStatus: null,
	isLoading: false,
	error: null,
};

export const useParkingWizardStore = create<ParkingWizardState & ParkingWizardActions>(set => ({
	...initialState,

	setStep: step => set({ step }),

	setLocation: location =>
		set({
			locationId: location.id,
			location,
			vehicles: [],
			availability: [],
			quote: null,
			totalPrice: null,
		}),

	addVehicle: vehicle =>
		set(state => ({
			vehicles: [...state.vehicles, vehicle],
			quote: null,
			totalPrice: null,
		})),

	removeVehicle: index =>
		set(state => ({
			vehicles: state.vehicles.filter((_, i) => i !== index),
			quote: null,
			totalPrice: null,
		})),

	updateVehicle: (index, vehicle) =>
		set(state => ({
			vehicles: state.vehicles.map((v, i) => (i === index ? vehicle : v)),
			quote: null,
			totalPrice: null,
		})),

	setAvailability: availability => set({ availability }),

	setQuote: (vehicles, totalPrice) =>
		set({
			quote: vehicles,
			totalPrice,
		}),

	setVehicleDetails: vehicleDetails => set({ vehicleDetails }),

	setLocationNames: locationNames =>
		set(state => ({
			locationNames: { ...state.locationNames, ...locationNames },
		})),

	setVehicleTypeNames: vehicleTypeNames =>
		set(state => ({
			vehicleTypeNames: { ...state.vehicleTypeNames, ...vehicleTypeNames },
		})),

	setReservationResult: (reservationId, reservationIds, transactionId, humanReadableId, humanReadableIds) =>
		set({
			reservationId,
			reservationIds,
			transactionId,
			humanReadableId,
			humanReadableIds,
		}),

	setNote: note => set({ note }),
	setSelectedUserId: selectedUserId => set({ selectedUserId }),
	setSelectedCustomer: selectedCustomer => set({ selectedCustomer }),
	lockCustomer: () => set({ customerLocked: true }),
	setOriginalPrice: originalPrice => set({ originalPrice }),
	setOriginalVehicles: originalVehicles => set({ originalVehicles: [...originalVehicles] }),
	setVehicleAvailabilityStatus: vehicleAvailabilityStatus => set({ vehicleAvailabilityStatus }),
	setLoading: isLoading => set({ isLoading }),
	setError: error => set({ error }),
	reset: () => set(initialState),
}));
